import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Database, Globe, Mail } from 'lucide-react';
import ContactModal from '../components/ContactModal';
import StandardFooter from '../components/StandardFooter';

const PrivacyPage = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page">
      {/* Header */}
      <header className="legal-header">
        <div className="legal-header-content">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
          <img src="/logo.png" alt="Offworld Ventures" className="legal-logo" />
        </div>
      </header>

      {/* Hero Section */}
      <section className="legal-hero">
        <h1 className="legal-title">Privacy Policy</h1>
        <p className="legal-subtitle">Your data. Your control. Our commitment.</p>
        <div className="legal-meta">
          <span>Effective Date: December 31, 2024</span>
          <span className="legal-divider">•</span>
          <span>Last Updated: December 31, 2024</span>
        </div>
      </section>

      {/* Content */}
      <section className="legal-content">
        <div className="legal-container">
          
          {/* Introduction */}
          <div className="legal-section">
            <div className="legal-section-header">
              <Eye size={24} />
              <h2>Introduction</h2>
            </div>
            <p>
              At Offworld Ventures, LLC ("Offworld," "we," "us," or "our"), we believe privacy is a fundamental right. This Privacy Policy explains how we collect, use, disclose, and protect your information when you visit our website or interact with our services.
            </p>
            <p>
              We're committed to transparency. If something isn't clear, reach out—we're here to help.
            </p>
          </div>

          {/* Information We Collect */}
          <div className="legal-section">
            <div className="legal-section-header">
              <Database size={24} />
              <h2>Information We Collect</h2>
            </div>
            
            <div className="legal-subsection">
              <h3>Information You Provide</h3>
              <ul>
                <li><strong>Contact Information:</strong> Name, email address, phone number, and company details when you reach out or inquire about our services.</li>
                <li><strong>Project Information:</strong> Details about your business, product requirements, technical needs, and project scope when engaging with us.</li>
                <li><strong>Communications:</strong> Content of messages, emails, and correspondence with our team.</li>
              </ul>
            </div>

            <div className="legal-subsection">
              <h3>Information Collected Automatically</h3>
              <ul>
                <li><strong>Usage Data:</strong> Pages visited, time spent, links clicked, and navigation patterns.</li>
                <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers.</li>
                <li><strong>Cookies & Similar Technologies:</strong> We use cookies to enhance your experience. You can control cookie preferences through your browser settings.</li>
              </ul>
            </div>
          </div>

          {/* How We Use Your Information */}
          <div className="legal-section">
            <div className="legal-section-header">
              <Lock size={24} />
              <h2>How We Use Your Information</h2>
            </div>
            <p>We use your information to:</p>
            <ul>
              <li>Evaluate project opportunities and communicate with clients</li>
              <li>Provide information about our studio, services, and capabilities</li>
              <li>Improve our website, services, and user experience</li>
              <li>Send updates, newsletters, and relevant industry insights (with your consent)</li>
              <li>Comply with legal obligations and protect our rights</li>
              <li>Prevent fraud, security threats, and misuse of our services</li>
            </ul>
          </div>

          {/* Information Sharing */}
          <div className="legal-section">
            <div className="legal-section-header">
              <Globe size={24} />
              <h2>Information Sharing & Disclosure</h2>
            </div>
            <p>We don't sell your personal information. We may share your information with:</p>
            
            <div className="legal-subsection">
              <h3>Service Providers</h3>
              <p>Third-party vendors who help us operate our business (e.g., hosting, analytics, email). These providers are bound by confidentiality agreements.</p>
            </div>

            <div className="legal-subsection">
              <h3>Business Transfers</h3>
              <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</p>
            </div>

            <div className="legal-subsection">
              <h3>Legal Requirements</h3>
              <p>When required by law, regulation, legal process, or governmental request, or to protect the rights, property, or safety of Offworld, our users, or others.</p>
            </div>

            <div className="legal-subsection">
              <h3>With Your Consent</h3>
              <p>We may share information with third parties when you explicitly consent to such sharing.</p>
            </div>
          </div>

          {/* Data Security */}
          <div className="legal-section">
            <div className="legal-section-header">
              <Shield size={24} />
              <h2>Data Security</h2>
            </div>
            <p>
              We implement industry-standard security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. This includes:
            </p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Employee training on data protection practices</li>
            </ul>
            <p>
              However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security but continuously work to protect your data.
            </p>
          </div>

          {/* Your Rights */}
          <div className="legal-section">
            <div className="legal-section-header">
              <Eye size={24} />
              <h2>Your Privacy Rights</h2>
            </div>
            <p>Depending on your location, you may have the following rights:</p>
            <ul>
              <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
              <li><strong>Opt-Out:</strong> Opt out of marketing communications at any time</li>
              <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Object:</strong> Object to certain processing activities</li>
            </ul>
            <p>
              To exercise these rights, contact us at the information provided below.
            </p>
          </div>

          {/* Cookies */}
          <div className="legal-section">
            <div className="legal-section-header">
              <Database size={24} />
              <h2>Cookies & Tracking Technologies</h2>
            </div>
            <p>
              We use cookies and similar technologies to enhance your browsing experience, analyze site traffic, and understand user behavior. You can control cookies through your browser settings, but disabling them may affect website functionality.
            </p>
            <div className="legal-subsection">
              <h3>Types of Cookies We Use:</h3>
              <ul>
                <li><strong>Essential Cookies:</strong> Required for website functionality</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              </ul>
            </div>
          </div>

          {/* Children's Privacy */}
          <div className="legal-section">
            <div className="legal-section-header">
              <Shield size={24} />
              <h2>Children's Privacy</h2>
            </div>
            <p>
              Our services are not directed to individuals under 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </div>

          {/* Changes to Policy */}
          <div className="legal-section">
            <div className="legal-section-header">
              <Lock size={24} />
              <h2>Changes to This Policy</h2>
            </div>
            <p>
              We may update this Privacy Policy from time to time. We'll notify you of significant changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of our services after changes constitutes acceptance of the updated policy.
            </p>
          </div>

          {/* Contact */}
          <div className="legal-section legal-contact">
            <div className="legal-section-header">
              <h2>Contact Us</h2>
            </div>
            <p>
              Questions, concerns, or requests regarding this Privacy Policy? We're here to help.
            </p>
            <div className="contact-info">
              <div className="contact-item">
                <strong>Company:</strong> Offworld Ventures, LLC
              </div>
              <div className="contact-item">
                <strong>Location:</strong> Based on Earth - For Now
              </div>
            </div>
            <button className="contact-cta-button" onClick={() => setIsContactOpen(true)}>
              <Mail size={20} />
              Get in Touch
            </button>
          </div>

        </div>
      </section>

      {/* Footer */}
      <StandardFooter onContactClick={() => setIsContactOpen(true)} />

      {/* Contact Modal */}
      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </div>
  );
};

export default PrivacyPage;